import {
  createAction,
  createAsyncThunk,
  createEntityAdapter,
  createSelector,
  createSlice,
  EntityState,
  PayloadAction,
} from "@reduxjs/toolkit"
import deepMerge from "lodash.merge"

import {
  CancelOrderProps,
  DataStatus,
  Driver,
  ManualOrder,
  MoveOrderToBufferProps,
  Order,
  OrderReturnProps,
  ReassignOrderProps,
  RootState,
  Team,
} from "@/types"
import {
  fetchCancelOrder,
  fetchEditOrder,
  fetchMoveOrderToBuffer,
  fetchOrderAssign,
  fetchOrderReturn,
  fetchOrders,
  fetchReassignOrder,
  getUniqueValues,
  isNumber,
  OrderUtils,
} from "@/utils"
import { handleStandardError } from "@/utils/errors"

import { selectDriversEntities } from "./drivers"
import { setDate } from "./global"
import { setCurrentTeam } from "./teams"
import { clearDataOnLogout } from "./user"

const ordersSortComparer = (a: Order | undefined, b: Order | undefined): 1 | -1 =>
  OrderUtils.getTime(a!) < OrderUtils.getTime(b!) ? -1 : 1

const ordersAdapter = createEntityAdapter<Order>({ sortComparer: ordersSortComparer })

type OrdersState = EntityState<Order> & {
  status: DataStatus | "updating" | null
}

const initialState: OrdersState = ordersAdapter.getInitialState({
  status: "no_team",
})

export const getOrders = createAsyncThunk(
  "orders/getOrders",
  async ({ teamId, date }: { teamId: Team["id"]; date: string }) => {
    const { response, json } = await fetchOrders(teamId, date)

    return {
      ok: response.ok,
      status: response.status,
      data: json.data,
    }
  }
)

export const requestCancelOrder = createAsyncThunk(
  "orders/requestCancel",
  async ({ orderId, body }: CancelOrderProps, { dispatch }) => {
    const { response, json } = await fetchCancelOrder({ orderId, body })

    const { data: order } = json

    if (!response.ok || !order) {
      handleStandardError(response, json)

      return {
        ok: response.ok,
        status: response.status,
      }
    }

    dispatch(updateOrder(order))

    return {
      ok: response.ok,
      status: response.status,
      data: order,
    }
  }
)

export const requestReassignOrder = createAsyncThunk(
  "orders/reassignOrder",
  async ({ orderId, body }: ReassignOrderProps, { dispatch }) => {
    const { response, json } = await fetchReassignOrder({
      orderId,
      body,
    })

    const { data: order } = json

    if (!response.ok || !order) {
      handleStandardError(response, json)

      return {
        ok: response.ok,
        status: response.status,
      }
    }

    dispatch(updateOrder(order))

    return {
      ok: response.ok,
      status: response.status,
      data: order,
    }
  }
)

export const requestAssignOrder = createAsyncThunk("orders/assignOrder", async (orderId: Order["id"], { dispatch }) => {
  const { response, json } = await fetchOrderAssign(orderId)

  const { data: order } = json

  if (!response.ok || !order) {
    handleStandardError(response, json)

    return {
      ok: response.ok,
      status: response.status,
    }
  }

  dispatch(updateOrder(order))

  return {
    ok: response.ok,
    status: response.status,
    data: order,
  }
})

export const requestOrderReturn = createAsyncThunk(
  "orders/requestReturn",
  async ({ orderId, body }: OrderReturnProps, { dispatch }) => {
    const { response, json } = await fetchOrderReturn({ orderId, body })

    const { data: order } = json

    if (!response.ok || !order) {
      handleStandardError(response, json)

      return {
        ok: response.ok,
        status: response.status,
      }
    }

    dispatch(updateOrder(order))

    return {
      ok: response.ok,
      status: response.status,
      data: order,
    }
  }
)

export const requestMoveOrderToBuffer = createAsyncThunk(
  "orders/requestMoveToBuffer",
  async ({ orderId, body }: MoveOrderToBufferProps, { dispatch }) => {
    const { response, json } = await fetchMoveOrderToBuffer({ orderId, body })

    const { data: order } = json

    if (!response.ok || !order) {
      handleStandardError(response, json)

      return {
        ok: response.ok,
        status: response.status,
      }
    }

    dispatch(updateOrder(order))

    return {
      ok: response.ok,
      status: response.status,
      data: order,
    }
  }
)

export const requestEditOrder = createAsyncThunk(
  "orders/requestEditOrder",
  async (
    { teamId, orderId, body }: { teamId: Team["id"]; orderId: Order["id"]; body: Partial<ManualOrder> },
    { dispatch }
  ) => {
    const { response, json } = await fetchEditOrder(teamId, orderId, body)

    const { data: order } = json

    if (!response.ok || !order) {
      handleStandardError(response, json)

      return {
        ok: response.ok,
        status: response.status,
      }
    }

    dispatch(updateOrder(order))

    return {
      ok: response.ok,
      status: response.status,
      data: order,
    }
  }
)

export const updateOrders = createAction<Order[]>("orders/updateOrders")

export const ordersReducer = (state = initialState, action: PayloadAction<Order | Order[]>) => {
  if (updateOrders.match(action)) {
    if (state.status !== "fetched") return state

    const orderUpdates = action.payload
    const updatedEntities = { ...state.entities }

    orderUpdates.forEach((orderUpdate) => {
      const orderInState = updatedEntities[orderUpdate.id]
      if (orderInState) {
        updatedEntities[orderUpdate.id] = deepMerge({}, updatedEntities[orderUpdate.id], orderUpdate)
      } else {
        const isFullEntity = Boolean(orderUpdate.team_id)
        if (isFullEntity) {
          // Delete order if it's canceled and without driver (867543194)
          if (orderUpdate.state === "canceled" && !orderUpdate.driver_id) {
            delete updatedEntities[orderUpdate.id]
          }

          updatedEntities[orderUpdate.id] = deepMerge({}, updatedEntities[orderUpdate.id], orderUpdate)
        }
      }
    })

    return { ...state, entities: updatedEntities, ids: getSortedOrdersIds(updatedEntities) }
  }

  return ordersSlice.reducer(state, action)
}

function getSortedOrdersIds(entities: OrdersState["entities"]) {
  return Object.values(entities)
    .sort(ordersSortComparer)
    .map((order) => order!.id)
}

function upsertDeepMergedOrder(state: EntityState<Order>, orderUpdate: Order) {
  const orderInState = state.entities[orderUpdate.id]

  if (orderInState) {
    ordersAdapter.updateOne(state, { id: orderUpdate.id, changes: deepMerge({}, orderInState, orderUpdate) })
  } else {
    const isFullEntity = Boolean(orderUpdate.team_id)
    if (isFullEntity) ordersAdapter.addOne(state, orderUpdate)
  }
}

const ordersSlice = createSlice({
  name: "orders",
  initialState,
  reducers: {
    updateOrder(state, action: PayloadAction<Order>) {
      const orderUpdate = action.payload

      upsertDeepMergedOrder(state, orderUpdate)
    },
  },
  extraReducers: (builder) => {
    builder.addCase(getOrders.pending, (state) => {
      if (state.ids.length) {
        state.status = "updating"
      } else {
        state.status = "loading"
      }
    })

    builder.addCase(getOrders.fulfilled, (state, { payload }) => {
      if (payload.ok) {
        if (payload.data) ordersAdapter.upsertMany(state, payload.data)
        state.status = "fetched"
      } else {
        state.status = "error"
      }
    })

    builder.addCase(setCurrentTeam, (state) => {
      ordersAdapter.removeAll(state)
    })
    builder.addCase(setDate, (state) => {
      ordersAdapter.removeAll(state)
    })
    builder.addCase(clearDataOnLogout, () => initialState)
  },
})

const { selectById, selectIds, selectAll } = ordersAdapter.getSelectors((state: RootState) => state.orders)

export const selectOrdersIds = selectIds

export const selectOrders = selectAll

export const selectOrderById = (id: Order["id"]) => (state: RootState) => selectById(state, id)

export const selectOrderIdKey = <T extends keyof Order>(id: Order["id"], key: T) =>
  createSelector(selectOrderById(id), (order) => order?.[key])

export const selectReturnedOrderIdData = (id: Order["id"]) =>
  createSelector(selectOrderById(id), (order) => {
    if (!order || !order.returned_order_id || !order.returned_order_uid) return null

    return {
      id: order.returned_order_id,
      uid: order.returned_order_uid,
      driver_id: order.returned_order_driver_id,
      pickup_at: order.returned_order_pickup_at,
    }
  })

export const selectOrderPoint = (id: Order["id"], point: "pickup" | "delivery") =>
  createSelector(selectOrderById(id), (order) => order?.[point])

export const selectOrdersWithDrivers = createSelector(selectOrders, selectDriversEntities, (orders, driversEntities) =>
  orders.map((order) => ({ ...order, driver: (order.driver_id && driversEntities[order.driver_id]) || null }))
)

export const selectFilteredOrders = (...filters: Array<(order: Order) => boolean>) =>
  createSelector(selectOrders, (orders) => orders.filter((order) => filters.every((filter) => filter(order))))

export const selectFilteredOrdersIds = (...filters: Array<(order: Order) => boolean>) =>
  createSelector(selectFilteredOrders(...filters), (orders) => orders.map((order) => order.id))

export const selectFilteredDriversIdsForFilteredOrders = (
  ordersFilters: Array<(order: Order) => boolean>,
  driversFilters: Array<(driver: Driver) => boolean>
) =>
  createSelector(selectFilteredOrders(...ordersFilters), selectDriversEntities, (orders, driversEntities) =>
    getUniqueValues(orders.map((order) => order.driver_id).filter(isNumber))
      .map((driverId) => driversEntities[driverId])
      .filter((driver) => !!driver && driversFilters.every((filter) => filter(driver)))
      .map((driver) => driver!.id)
  )

export const selectFilteredOrdersWithDrivers = (...filters: Array<(order: Order) => boolean>) =>
  createSelector(selectOrdersWithDrivers, (orders) =>
    orders.filter((order) => filters.every((filter) => filter(order)))
  )

export const selectFilteredOrdersWithDriversIds = (...filters: Array<(order: Order) => boolean>) =>
  createSelector(selectFilteredOrdersWithDrivers(...filters), (orders) => orders.map((order) => order.id))

export const selectSortedFilteredOrdersWithDriversIds = (
  sorter: ((a: Order, b: Order) => number) | null,
  ...filters: Array<(order: Order) => boolean>
) =>
  createSelector(selectFilteredOrdersWithDrivers(...filters), (orders) => {
    return sorter ? orders.sort(sorter).map((order) => order.id) : orders.map((order) => order.id)
  })

export const selectOrdersByDriverId = (driverId: Driver["id"]) =>
  createSelector(selectOrders, (orders) => orders.filter((order) => order.driver_id === driverId))

export const selectOrdersIdsByDriverId = (driverId: Driver["id"]) =>
  createSelector(selectOrdersByDriverId(driverId), (orders) => orders.map((order) => order.id))

export const selectFilteredOrdersByDriverId = (driverId: Driver["id"], ...filters: Array<(order: Order) => boolean>) =>
  createSelector(selectFilteredOrders(...filters), (orders) => orders.filter((order) => order.driver_id === driverId))

export const selectFilteredOrdersIdsByDriverId = (
  driverId: Driver["id"],
  ...filters: Array<(order: Order) => boolean>
) => createSelector(selectFilteredOrdersByDriverId(driverId, ...filters), (orders) => orders.map((order) => order.id))

export const selectActiveOrdersByDriverId = (driverId: Driver["id"]) =>
  createSelector(selectOrdersByDriverId(driverId), (orders) => orders.filter((order) => OrderUtils.isAssigned(order)))

export const selectActiveOrdersIdsByDriverId = (driverId: Driver["id"]) =>
  createSelector(selectActiveOrdersByDriverId(driverId), (orders) => orders.map((order) => order.id))

export const selectCurrentOrderOfDriver = (driverId: Driver["id"]) =>
  createSelector(selectOrdersByDriverId(driverId), (orders) =>
    orders.find((order) => order.state === "pending" || order.state === "started")
  )

export const selectCurrentOrderIdOfDriver = (driverId: Driver["id"]) =>
  createSelector(selectCurrentOrderOfDriver(driverId), (order) => order?.id)

export const selectOrdersStatus = (state: RootState) => state.orders.status

export const { updateOrder } = ordersSlice.actions
